import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '../../services/authentication.service';

@Component({
  selector: 'app-notification-management',
  templateUrl: './notification-management.component.html',
  styleUrls: ['./notification-management.component.scss'],
})
export class NotificationManagementComponent implements OnInit {
  notificationForm: FormGroup;
  isSubmitting = false;
  responseSummary: any = null;
  selectedTarget = 'all';
  draftSaved = false;
  previewFocused = false;
  showOptionalData = false;
  isLoadingRecipients = false;
  channelOptions = [
    { value: 'push', label: 'Push notification' },
    { value: 'email', label: 'Email' },
  ];
  isLoadingMoreRecipients = false;
  recipientOptions: Array<{ value: string; label: string; userId: string }> = [];
  recipientSearch = '';
  recipientPage = 0;
  recipientHasMore = true;
  private draftTimer: any;

  activeTab: 'send' | 'history' = 'send';
  historyData: any[] = [];
  historyLoading = false;
  historyPage = 1;
  historyTotal = 0;
  historyLimit = 15;

  constructor(
    private fb: FormBuilder,
    private authenticationService: AuthenticationService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.notificationForm = this.fb.group({
      targetType: ['all', Validators.required],
      title: ['', [Validators.required, Validators.maxLength(80)]],
      body: ['', [Validators.required, Validators.maxLength(500)]],
      data: [''],
      channels: ['push', Validators.required],
      includeInactiveUsers: [false],
      onlyInactiveUsers: [false],
      emailSubject: [''],
      emailHtml: [''],
      recipientEmail: [''],
      userIds: [''],
      fcmTokens: [''],
    });

    const savedDraft = localStorage.getItem('notification-draft');
    if (savedDraft) {
      try {
        const draftValue = JSON.parse(savedDraft);
        this.notificationForm.patchValue(draftValue);
        this.draftSaved = true;
      } catch (error) {
        localStorage.removeItem('notification-draft');
      }
    }

    this.notificationForm.get('targetType')?.valueChanges.subscribe((value) => {
      this.selectedTarget = value || 'all';
      this.updateFieldValidators(value);
      this.syncPreview();
      this.cdr.detectChanges();
    });

    this.notificationForm.valueChanges.subscribe(() => {
      this.syncPreview();
      this.autoSaveDraft();
      this.cdr.detectChanges();
    });

    this.notificationForm.get('recipientEmail')?.valueChanges.subscribe(() => {
      if (this.selectedTarget === 'single' && this.recipientSearch !== this.notificationForm.get('recipientEmail')?.value) {
        this.recipientSearch = this.notificationForm.get('recipientEmail')?.value || '';
        this.loadRecipients(true);
      }
    });

    this.updateFieldValidators(this.notificationForm.get('targetType')?.value);
    this.syncPreview();
    this.loadRecipients();
  }

  get canSubmit(): boolean {
    return this.notificationForm.valid && !this.isSubmitting;
  }

  get titleLength(): number {
    return this.notificationForm.get('title')?.value?.length || 0;
  }

  get bodyLength(): number {
    return this.notificationForm.get('body')?.value?.length || 0;
  }

  get recipientSummary(): string {
    switch (this.selectedTarget) {
      case 'single':
        return 'Specific user';
      case 'users':
        return 'Selected users';
      case 'tokens':
        return 'Device tokens';
      default:
        return 'All users';
    }
  }

  get previewTitle(): string {
    return this.notificationForm.get('title')?.value?.trim() || 'Your premium update is ready';
  }

  get previewBody(): string {
    return this.notificationForm.get('body')?.value?.trim() || 'A polished message will appear here as you type.';
  }

  get previewBadge(): string {
    return this.selectedTarget === 'all' ? 'Broadcast' : 'Targeted';
  }

  get selectedChannel(): string {
    return this.notificationForm?.get('channels')?.value || 'push';
  }

  hasError(controlName: string, errorName: string): boolean {
    const control = this.notificationForm.get(controlName);
    return !!(control && control.touched && control.hasError(errorName));
  }

  onSubmit(): void {
    if (this.notificationForm.invalid) {
      this.notificationForm.markAllAsTouched();
      this.toastr.error('Please complete the required fields before sending the notification.', 'Validation Error');
      return;
    }

    const formValue = this.notificationForm.getRawValue();
    if (formValue.targetType === 'single') {
      const recipientEmail = formValue.recipientEmail?.trim();
      if (!recipientEmail) {
        this.toastr.error('Please select a recipient from the list.', 'Validation Error');
        return;
      }

      const selectedRecipient = this.recipientOptions.find((option) => option.value === recipientEmail);
      if (!selectedRecipient?.userId) {
        this.isSubmitting = false;
        this.toastr.error('The selected recipient could not be resolved.', 'Recipient Not Found');
        return;
      }

      this.isSubmitting = true;
      this.responseSummary = null;
      this.submitBroadcast(this.buildPayload(selectedRecipient.userId));
      return;
    }

    this.isSubmitting = true;
    this.responseSummary = null;
    this.submitBroadcast(this.buildPayload());
  }

  private submitBroadcast(payload: any): void {
    if (!payload) {
      return;
    }

    this.authenticationService.broadcastNotification(payload).subscribe(
      (res: any) => {
        this.isSubmitting = false;
        this.responseSummary = this.extractSummary(res, this.selectedChannel);
        this.toastr.success(res?.message || 'Notification broadcast has been queued successfully.', 'Success');
        localStorage.removeItem('notification-draft');
        this.draftSaved = false;
      },
      (error: any) => {
        this.isSubmitting = false;
        const message = error?.error?.message || error?.message || 'Unable to send the notification right now.';
        this.toastr.error(message, 'Broadcast Failed');
      },
    );
  }

  saveDraft(): void {
    const draftValue = this.notificationForm.getRawValue();
    localStorage.setItem('notification-draft', JSON.stringify(draftValue));
    this.draftSaved = true;
    this.toastr.success('Draft saved locally.', 'Saved');
  }

  resetForm(): void {
    this.notificationForm.reset({
      targetType: 'all',
      title: '',
      body: '',
      data: '',
      channels: 'push',
      includeInactiveUsers: false,
      onlyInactiveUsers: false,
      emailSubject: '',
      emailHtml: '',
      recipientEmail: '',
      userIds: '',
      fcmTokens: '',
    });
    this.selectedTarget = 'all';
    this.showOptionalData = false;
    this.draftSaved = false;
    this.recipientSearch = '';
    this.recipientPage = 0;
    this.recipientHasMore = true;
    this.updateFieldValidators('all');
    this.syncPreview();
    localStorage.removeItem('notification-draft');
  }

  previewAction(): void {
    this.previewFocused = !this.previewFocused;
    this.toastr.info('Preview updates automatically as you type.', 'Live Preview');
  }

  toggleOptionalData(): void {
    this.showOptionalData = !this.showOptionalData;
  }

  setTab(tab: 'send' | 'history'): void {
    this.activeTab = tab;
    if (tab === 'history') {
      this.loadHistory();
    }
  }

  loadHistory(page: number = 1): void {
    this.historyPage = page;
    this.historyLoading = true;
    this.authenticationService.getBroadcastHistory(this.historyPage, this.historyLimit).subscribe({
      next: (res) => {
        this.historyLoading = false;
        if (res && res.data) {
          this.historyData = res.data;
          this.historyTotal = res.meta?.total || 0;
        }
      },
      error: (err) => {
        this.historyLoading = false;
        this.toastr.error('Failed to load notification history');
      }
    });
  }

  changeHistoryPage(page: number): void {
    this.loadHistory(page);
  }

  loadRecipients(reset = false): void {
    if (this.selectedTarget !== 'single') {
      return;
    }

    if (reset) {
      this.recipientPage = 0;
      this.recipientHasMore = true;
      this.recipientOptions = [];
    }

    if (!this.recipientHasMore && !reset) {
      return;
    }

    this.isLoadingRecipients = true;
    const start = reset ? 0 : this.recipientPage * 50;
    this.authenticationService.getUsersList({
      search: { value: this.recipientSearch || '' },
      order: [{ column: 0, dir: 'asc' }],
      start,
      length: 50,
      draw: 1,
    }, false).subscribe((resp: any) => {
      const users = resp?.user?.[0]?.data || [];
      const mappedUsers = users.map((user: any) => {
        const email = user.email || user.userEmail || user.emailAddress || '';
        return {
          value: email,
          label: email ? `${email}${user.userName ? ` · ${user.userName}` : ''}` : 'Unknown user',
          userId: user._id || user.id || user.userId || '',
        };
      }).filter((option: any) => option.value);

      this.recipientOptions = reset ? mappedUsers : [...this.recipientOptions, ...mappedUsers];
      this.recipientPage += 1;
      this.recipientHasMore = mappedUsers.length === 50;
      this.isLoadingRecipients = false;
      this.isLoadingMoreRecipients = false;
      this.cdr.detectChanges();
    }, () => {
      this.isLoadingRecipients = false;
      this.isLoadingMoreRecipients = false;
      this.recipientOptions = [];
      this.recipientHasMore = false;
      this.cdr.detectChanges();
    });
  }

  loadMoreRecipients(): void {
    if (this.isLoadingRecipients || this.isLoadingMoreRecipients || !this.recipientHasMore) {
      return;
    }
    this.isLoadingMoreRecipients = true;
    this.loadRecipients();
  }

  private autoSaveDraft(): void {
    clearTimeout(this.draftTimer);
    this.draftTimer = setTimeout(() => {
      const draftValue = this.notificationForm.getRawValue();
      localStorage.setItem('notification-draft', JSON.stringify(draftValue));
      this.draftSaved = true;
    }, 400);
  }

  private syncPreview(): void {
    this.selectedTarget = this.notificationForm.get('targetType')?.value || 'all';
  }

  private updateFieldValidators(targetType: string): void {
    const recipientEmailControl = this.notificationForm.get('recipientEmail');
    const userIdsControl = this.notificationForm.get('userIds');
    const fcmTokensControl = this.notificationForm.get('fcmTokens');

    recipientEmailControl?.clearValidators();
    userIdsControl?.clearValidators();
    fcmTokensControl?.clearValidators();

    switch (targetType) {
      case 'single':
        recipientEmailControl?.setValidators([Validators.required]);
        break;
      case 'users':
        userIdsControl?.setValidators([Validators.required]);
        break;
      case 'tokens':
        fcmTokensControl?.setValidators([Validators.required]);
        break;
      default:
        break;
    }

    recipientEmailControl?.updateValueAndValidity();
    userIdsControl?.updateValueAndValidity();
    fcmTokensControl?.updateValueAndValidity();
  }

  private buildPayload(resolvedUserId?: string): any {
    const formValue = this.notificationForm.getRawValue();
    const payload: any = {
      title: formValue.title?.trim(),
      body: formValue.body?.trim(),
      target: formValue.targetType,
      channels: formValue.channels ? [formValue.channels] : [],
      includeInactiveUsers: !!formValue.includeInactiveUsers,
      onlyInactiveUsers: !!formValue.onlyInactiveUsers,
      emailSubject: formValue.emailSubject?.trim() || formValue.title?.trim(),
      emailHtml: formValue.emailHtml?.trim() || `<p>${formValue.body?.trim() || ''}</p>`,
    };

    if (formValue.data && formValue.data.trim()) {
      try {
        payload.data = JSON.parse(formValue.data);
      } catch (error) {
        this.toastr.error('Please provide valid JSON for the optional data field.', 'Invalid JSON');
        return null;
      }
    }

    switch (formValue.targetType) {
      case 'single':
        payload.userId = resolvedUserId || formValue.recipientEmail?.trim();
        break;
      case 'users':
        payload.userIds = formValue.userIds
          ?.split(',')
          .map((value: string) => value.trim())
          .filter(Boolean);
        break;
      case 'tokens':
        payload.fcmTokens = formValue.fcmTokens
          ?.split(',')
          .map((value: string) => value.trim())
          .filter(Boolean);
        break;
      default:
        break;
    }

    return payload;
  }

  private extractSummary(response: any, channel?: string): any {
    const totals = response?.totals || response?.data?.totals || response?.data?.summary || response?.summary || response?.result || response;
    const channelName = (channel || this.selectedChannel || 'push').toLowerCase();
    const channelTotals = totals?.[channelName] || totals?.[channelName === 'push' ? 'push' : 'email'] || null;

    const total = totals?.total ?? totals?.count ?? totals?.totalCount ?? response?.total ?? response?.count ?? 0;
    const success = channelTotals?.success ?? channelTotals?.successful ?? totals?.success ?? totals?.successful ?? response?.success ?? response?.successful ?? 0;
    const failed = channelTotals?.failed ?? channelTotals?.failure ?? totals?.failed ?? totals?.failure ?? response?.failed ?? response?.failure ?? 0;

    return {
      total,
      success,
      failed,
    };
  }
}
